import {
  browserSessionPersistence,
  setPersistence,
  signInWithCustomToken,
} from 'firebase/auth'
import { auth } from '@/lib/firebase'
import { TEST_USER_SEARCH_PARAM } from './account-v2-test-user-search'

interface TestAuthUserView {
  uid: string
  email: string
  display_name: string
  created_at: string
  last_login_at: string | null
}

interface CreateTestUserResponse {
  user: TestAuthUserView
  default_route: string
}

interface MintTokenResponse {
  custom_token: string
  user: TestAuthUserView
  default_route: string
}

const TEST_AUTH_FLAG = import.meta.env.VITE_TEST_AUTH === '1'

export function isAccountV2TestAuthEnabled(): boolean {
  return TEST_AUTH_FLAG && import.meta.env.VITE_USE_MOCKS !== '1'
}

function apiUrl(path: string): string {
  const base = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '')
  if (!base) throw new Error('VITE_API_URL is required for account-v2 test auth')
  return `${base}${path}`
}

async function fetchJson<T>(path: string, init: RequestInit): Promise<T> {
  const response = await fetch(apiUrl(path), {
    ...init,
    headers: {
      accept: 'application/json',
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...init.headers,
    },
  })

  if (!response.ok) {
    const text = await response.text().catch(() => '')
    throw new Error(`Account-v2 test-auth request failed (${response.status}): ${text}`)
  }

  return (await response.json()) as T
}

function withCurrentSearch(defaultRoute: string, uid: string): string {
  const target = new URL(defaultRoute, window.location.origin)
  const current = new URL(window.location.href)
  for (const [key, value] of current.searchParams.entries()) {
    if (key !== TEST_USER_SEARCH_PARAM && !target.searchParams.has(key)) {
      target.searchParams.set(key, value)
    }
  }
  target.searchParams.set(TEST_USER_SEARCH_PARAM, uid)
  return `${target.pathname}${target.search}${target.hash}`
}

async function createAndRedirectTestUser(): Promise<never> {
  const created = await fetchJson<CreateTestUserResponse>('/v2/test-auth/users', {
    method: 'POST',
  })
  window.location.replace(withCurrentSearch(created.default_route, created.user.uid))
  return await new Promise<never>(() => {})
}

async function signInTestUser(uid: string): Promise<void> {
  const minted = await fetchJson<MintTokenResponse>(
    `/v2/test-auth/users/${encodeURIComponent(uid)}/token`,
    { method: 'POST' },
  )
  await setPersistence(auth, browserSessionPersistence)
  await signInWithCustomToken(auth, minted.custom_token)
}

/**
 * Browser-only account-management sandbox auth.
 *
 * In account-v2 sandbox builds, `?testUser=<uid>` mints a Firebase custom token
 * through the deployed test-auth surface and signs in with session persistence.
 * When no uid is provided, the browser creates a test user and redirects to
 * the backend-provided default route with `testUser` attached.
 */
export async function bootAccountV2TestAuth(): Promise<void> {
  if (!isAccountV2TestAuthEnabled()) return

  const url = new URL(window.location.href)
  const uid = url.searchParams.get(TEST_USER_SEARCH_PARAM)
  if (!uid) {
    await createAndRedirectTestUser()
    return
  }

  await signInTestUser(uid)
}
